import { type ReactNode, StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { AUTH_STATUS } from "@/features/Auth/types/auth";
import axiosInstance, { setNavigationHandler } from "@/lib/axios";
import {
  captureError,
  initialize as initializeMonitoring,
} from "@/lib/monitoring";
import { setMonitoringUser } from "@/lib/monitoring/userTracking";
import { computeLoginNavigation } from "@/lib/redirect";
import {
  initialize as initializeTracking,
  setTrackingUser,
} from "@/lib/tracking";
import { isAuthenticatedAtom } from "@/store/authAtoms";
import { RouterProvider } from "@tanstack/react-router";
import i18next from "i18next";
import { LoaderCircle } from "lucide-react";
import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { pdfjs } from "react-pdf";
import { AppProviders } from "./AppProviders";
import { useUser } from "./network/useUser";
import { router } from "./router";
import { appStore } from "./store/jotaiStore";

const InitializedAuth = ({ children }: { children: ReactNode }) => {
  const [isReady, setIsReady] = useState(false);
  const { data: user, isLoading: isLoadingUser } = useUser();

  useEffect(() => {
    const initAuth = async () => {
      const store = appStore;
      try {
        const { data } = await axiosInstance.get("/auth/status");
        const isLoggedIn = data === AUTH_STATUS.LOGGED_IN;
        store.set(isAuthenticatedAtom, isLoggedIn);
      } catch {
        store.set(isAuthenticatedAtom, false);
      } finally {
        setIsReady(true);
      }
    };

    initAuth();
  }, []);

  // Set user in monitoring services whenever user data is available
  // This ensures user context is re-established after Sentry replay session restarts
  // (which occur after 15min inactivity or 60min max duration)
  useEffect(() => {
    if (!user) return;

    setMonitoringUser(user);
    setTrackingUser(user);
  }, [user]);

  if (!isReady || isLoadingUser) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <LoaderCircle className="animate-spin" size={48} />
      </div>
    );
  }

  return children;
};

// Initialize monitoring with router for Sentry integration
initializeMonitoring(router);
initializeTracking();

// Set up navigation handler for axios to avoid circular dependencies.
// Preserve where the user was headed so they return there after re-auth.
setNavigationHandler(() => {
  router.navigate(computeLoginNavigation(router.state.location));
});

// Set up react-pdf service worker
// This messiness is to work around https://github.com/wojtekmaj/react-pdf/issues/342
// where the worker JS file does not get cached in the browser for some reason.
const initializePdfWorker = async () => {
  const fetchResponse = await fetch(pdfjsWorkerUrl);
  const code = await fetchResponse.text();
  const codeBlob = new Blob([code], { type: "text/javascript" });
  const workerUrl = URL.createObjectURL(codeBlob);
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
};
initializePdfWorker().catch((error) => {
  captureError(error.error?.msg || "Error initializing pdfjs worker");
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

i18next.init({
  lng: "en",
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

const rootElement = document.getElementById("root");
if (rootElement && !rootElement.innerHTML) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <AppProviders>
        <InitializedAuth>
          <RouterProvider router={router} />
        </InitializedAuth>
      </AppProviders>
    </StrictMode>,
  );
}
